import React from 'react';
import {HangerIcon, HomeIcon, ShieldIcon, ShirtIcon, SparklesIcon} from '../icons';
import {ThemeToggle} from '../ThemeToggle';
import {BrowserMock, PhoneMock} from '../landing/AppMockup';
import {appName} from '../landing/content';

/**
 * Общий каркас публичных экранов авторизации: вход, регистрация, подтверждение
 * адреса и восстановление пароля (`docs/features/2026-08-20_auth-registration-recovery`).
 *
 * Слева — ценностная колонка (продолжение лендинга), справа — карточка с формой.
 * На `xs/sm/md` карточка идёт первой, мокап скрыт: форма остаётся в первом экране.
 *
 * Переключатель темы в шапке не только удобство: класс `dark` навешивает `useTheme`,
 * который монтируется вместе с ним — без него публичные экраны игнорировали выбор темы.
 */

const VALUE_POINTS = [
    {
        id: 'wardrobe',
        icon: HangerIcon,
        title: 'Умный гардероб',
        text: 'Структурируйте вещи, создавайте образы и планируйте покупки с умом.',
    },
    {
        id: 'stylist',
        icon: SparklesIcon,
        title: 'AI-стилист',
        text: 'Персональные рекомендации и готовые образы на каждый день и любой случай.',
    },
    {
        id: 'security',
        icon: ShieldIcon,
        title: 'Безопасный доступ',
        text: 'Ваши данные под защитой. Доступ к гардеробу выдаёте только вы.',
    },
];

interface AuthShellProps {
    /** Заголовок ценностной колонки — единственный `<h1>` экрана. */
    heading: React.ReactNode;
    lead: string;
    children: React.ReactNode;
}

const AuthShell: React.FC<AuthShellProps> = ({heading, lead, children}) => (
    <div className="min-h-screen flex flex-col bg-white dark:bg-ink-950" data-testid="auth-shell">
        <header className="flex items-center justify-between px-4 sm:px-8 h-16 shrink-0">
            <div className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-xl bg-brand-500 flex items-center justify-center">
                    <ShirtIcon className="w-5 h-5 text-ink-900"/>
                </span>
                <span className="text-lg font-bold text-ink-900 dark:text-ink-50">{appName()}</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
                <ThemeToggle/>
                <a
                    href="/"
                    className="inline-flex items-center gap-2 text-sm font-medium text-ink-600 dark:text-ink-300 hover:text-ink-900 dark:hover:text-white underline underline-offset-4 rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                    <HomeIcon className="w-4 h-4"/>
                    На главную
                </a>
            </div>
        </header>

        {/* `flex-1` только на lg+: на узких экранах колонки не должны растягиваться
            на всю высоту, иначе под формой появляется пустая полоса. */}
        <main className="lg:flex-1 grid lg:grid-cols-2">
            <section className="order-2 lg:order-1 px-4 sm:px-8 lg:px-12 py-10 lg:py-14 min-w-0">
                <h1 className="text-3xl sm:text-4xl xl:text-5xl font-bold leading-[1.1] text-ink-900 dark:text-ink-50">
                    {heading}
                </h1>
                <p className="mt-5 text-sm sm:text-base leading-7 text-ink-600 dark:text-ink-300 max-w-lg">
                    {lead}
                </p>

                <div className="hidden lg:block relative mt-10 mb-16 max-w-xl" aria-hidden="true">
                    <BrowserMock className="w-full"/>
                    <PhoneMock className="absolute -right-4 -bottom-8 w-32"/>
                </div>

                <ul className="mt-10 space-y-6 max-w-lg">
                    {VALUE_POINTS.map(point => (
                        <li key={point.id} className="flex gap-4 min-w-0">
                            <span className="w-12 h-12 shrink-0 rounded-full bg-brand-50 dark:bg-brand-500/10 flex items-center justify-center">
                                <point.icon className="w-5 h-5 text-brand-600 dark:text-brand-400"/>
                            </span>
                            <div className="min-w-0">
                                <h2 className="text-sm font-semibold text-ink-900 dark:text-ink-50">{point.title}</h2>
                                <p className="mt-1 text-sm leading-6 text-ink-500 dark:text-ink-400">{point.text}</p>
                            </div>
                        </li>
                    ))}
                </ul>
            </section>

            <div className="order-1 lg:order-2 flex items-start lg:items-center justify-center bg-ink-50 dark:bg-ink-900 lg:border-l border-ink-100 dark:border-white/5 px-4 sm:px-8 py-10 lg:py-14">
                <div className="card w-full max-w-md p-6 sm:p-8">
                    <div className="flex items-center justify-center gap-2">
                        <span className="w-9 h-9 rounded-xl bg-brand-500 flex items-center justify-center">
                            <ShirtIcon className="w-5 h-5 text-ink-900"/>
                        </span>
                        <span className="text-xl font-bold text-ink-900 dark:text-ink-50">{appName()}</span>
                    </div>
                    {children}
                </div>
            </div>
        </main>
    </div>
);

export default AuthShell;
